import { z } from "zod";
import { CatalogOutcomeSchema } from "./catalog-sidecar.js";
/**
 * A project's draft is the catalog's three documents, copied (workflows/V5-Plan.v2.md §4):
 * agent.yaml (the Claude Managed Agents agent object, verbatim text), the optional Foundry
 * overlay, and the sidecar pieces a run needs — the goal and the outcome. Revisions are
 * append-only; a run pins one.
 */
export const ProjectSidecarSchema = z.object({
    goal: z.string().max(20_000).optional(),
    outcome: CatalogOutcomeSchema.optional(),
});
export const ProjectRevisionSchema = z.object({
    projectId: z.string().min(1),
    revision: z.number().int().positive(),
    agentYaml: z.string(),
    overlayYaml: z.string().nullable(),
    sidecar: ProjectSidecarSchema,
    createdAt: z.string().datetime(),
});
export const AGENT_YAML_MAX_BYTES = 200_000;
export const OVERLAY_YAML_MAX_BYTES = 50_000;
/** Where a project's uploads mount inside every engine's container, read-only. */
export const UPLOADS_DIR = "/mnt/session/uploads";
export function uploadMountPath(name) {
    return `${UPLOADS_DIR}/${name}`;
}
/* ---------------- uploads and starter files ---------------- */
/** One upload may be this big; a project keeps at most this many live uploads. */
export const PROJECT_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const PROJECT_FILES_MAX = 50;
/**
 * A template ships at most this many starter files (its flat `files/` folder, each within
 * PROJECT_FILE_MAX_BYTES); a project created from it starts with them as uploads.
 */
export const STARTER_FILES_MAX = 20;
/**
 * What a save does when the project already holds a file of that name: `create` (the default)
 * refuses with 409 file_exists and writes nothing; `replace` replaces that file, for an explicit
 * replace (a person who confirmed it, or an edit of the file itself).
 */
export const ProjectFileSaveModeSchema = z.enum(["create", "replace"]);
/** The last path component, trimmed; null when nothing usable is left. */
export function cleanFileName(name) {
    const last = name.split(/[\\/]/).pop()?.trim() ?? "";
    if (!last || last === "." || last === ".." || last.length > 255 || last.includes("\0"))
        return null;
    return last;
}
/* ---------------- template upgrades ---------------- */
export const TemplateVersionRefSchema = z.object({
    id: z.string().uuid(),
    version: z.number().int().positive(),
});
/**
 * A team member the project pins (template-method review R1) whose latest live version would
 * change what the project's runs do: its agent.yaml differs, not only its catalog copy.
 */
export const MemberUpgradeSchema = z.object({
    /** The roster id, `<library_key>/<slug>`. */
    id: z.string().min(1),
    appliedVersion: TemplateVersionRefSchema,
    latestVersion: TemplateVersionRefSchema,
});
/**
 * Projects stay pinned to the version they were created from, and a team project to its
 * members' versions; this says whether a newer one would change the project. `upgradeAvailable`
 * is either kind: the template's own documents (`rootUpgradeAvailable`) or a pinned member
 * (`memberUpgrades`, a "member upgrade" when it is the only change). Applying the latest version
 * re-pins every member to its latest. The two details are absent from a server before member pins.
 */
export const TemplateUpgradeStatusSchema = z.object({
    projectId: z.string().uuid(),
    appliedVersion: TemplateVersionRefSchema.nullable(),
    latestVersion: TemplateVersionRefSchema.nullable(),
    upgradeAvailable: z.boolean(),
    rootUpgradeAvailable: z.boolean().optional(),
    memberUpgrades: z.array(MemberUpgradeSchema).optional(),
});
/**
 * One section of the three-way merge: a top-level agent.yaml key, `overlay`, `sidecar.goal`
 * or `sidecar.outcome`. `auto` = the target applies (the owner never touched it, or already
 * matches); `keep` = the owner's edit stays (the template did not change it); `conflict` =
 * both changed and a choice is needed.
 */
export const UpgradeSectionSchema = z.object({
    section: z.string().min(1),
    resolution: z.enum(["auto", "keep", "conflict"]),
    current: z.unknown(),
    target: z.unknown(),
});
export const TemplateUpgradePreviewSchema = z.object({
    targetVersion: TemplateVersionRefSchema,
    sections: z.array(UpgradeSectionSchema),
    conflicts: z.array(z.string()),
    autoApplicable: z.boolean(),
    /**
     * The target version's starter files the project lacks, by upload name: applying adds them
     * as uploads. A file the project already has (by name) is never overwritten, so it is not listed.
     */
    starterFiles: z.array(z.string().min(1)),
    /**
     * The pinned members whose latest version changes them: applying re-pins every member the
     * merged agent.yaml names to its latest. Absent from a server before member pins.
     */
    memberUpgrades: z.array(MemberUpgradeSchema).optional(),
});
export const UpgradeChoicesSchema = z.record(z.string(), z.enum(["current", "target"]));
//# sourceMappingURL=projects.js.map